import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import type { User, Session, Provider } from '@supabase/supabase-js';

// Supported OAuth providers (excluding Apple as requested)
export type AuthProvider = 'google' | 'facebook';

interface UserPreferences {
    theme: 'light' | 'dark' | 'oled';
    textSize: 'small' | 'normal' | 'large' | 'xlarge';
    notificationsEnabled: boolean;
    dailyReminderTime: string | null;
    songOfTheDayEnabled: boolean;
    midiVolume: number;
}

interface UserProfile {
    id: string;
    email: string;
    displayName: string | null;
    preferences: UserPreferences;
    createdAt: string;
}

interface AuthContextType {
    // State
    user: User | null;
    profile: UserProfile | null;
    session: Session | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    isConfigured: boolean;
    error: string | null;

    // Auth methods
    signInWithEmail: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
    signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<{ success: boolean; error?: string }>;
    signInWithMagicLink: (email: string) => Promise<{ success: boolean; error?: string }>;
    signInWithProvider: (provider: AuthProvider) => Promise<{ success: boolean; error?: string }>;
    signOut: () => Promise<void>;
    deleteAccount: () => Promise<{ success: boolean; error?: string }>;
    resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
    updatePassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>;

    // Profile methods
    updateProfile: (data: Partial<UserProfile>) => Promise<{ success: boolean; error?: string }>;
    updatePreferences: (prefs: Partial<UserPreferences>) => Promise<{ success: boolean; error?: string }>;

    // Sync methods
    syncLocalDataToCloud: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Translate Supabase error messages to French
function translateError(message: string): string {
    const translations: Record<string, string> = {
        'Invalid login credentials': 'Email ou mot de passe incorrect',
        'Email not confirmed': 'Veuillez confirmer votre email avant de vous connecter',
        'User already registered': 'Un compte existe déjà avec cet email',
        'Password should be at least 6 characters': 'Le mot de passe doit contenir au moins 6 caractères',
        'Unable to validate email address: invalid format': 'Format d\'email invalide',
        'Email rate limit exceeded': 'Trop de tentatives, veuillez réessayer plus tard',
        'For security purposes, you can only request this once every 60 seconds': 'Veuillez patienter 60 secondes avant de réessayer',
        'New password should be different from the old password': 'Le nouveau mot de passe doit être différent de l\'ancien',
    };
    return translations[message] || message;
}

const defaultPreferences: UserPreferences = {
    theme: 'dark',
    textSize: 'normal',
    notificationsEnabled: false,
    dailyReminderTime: null,
    songOfTheDayEnabled: false,
    midiVolume: 100
};

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const previousUserIdRef = useRef<string | null>(null);

    const isConfigured = isSupabaseConfigured();
    const isAuthenticated = !!user && !!session;

    // Fetch user profile from database
    const fetchProfile = useCallback(async (userId: string, userEmail: string) => {
        console.log('fetchProfile called with:', userId, userEmail);
        if (!supabase) {
            console.log('fetchProfile: supabase is null!');
            return null;
        }

        try {
            console.log('fetchProfile: querying profiles table...');

            // Add timeout to detect hanging queries
            const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(() => reject(new Error('Query timeout after 5s')), 5000);
            });

            const queryPromise = supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as any;

            console.log('fetchProfile result - data:', data?.display_name, 'error:', error?.message);

            if (error && error.code === 'PGRST116') {
                // Profile doesn't exist, create it (without email - column doesn't exist)
                const newProfile = {
                    id: userId,
                    display_name: userEmail.split('@')[0],
                    preferences: defaultPreferences
                };

                const { data: created, error: createError } = await supabase
                    .from('profiles')
                    .insert(newProfile)
                    .select()
                    .single();

                if (createError) {
                    console.error('Error creating profile:', createError);
                    return null;
                }

                return {
                    id: created.id,
                    email: userEmail,
                    displayName: created.display_name,
                    preferences: created.preferences || defaultPreferences,
                    createdAt: created.created_at
                };
            }

            if (error) {
                console.error('Error fetching profile:', error);
                return null;
            }

            return {
                id: data.id,
                email: userEmail,
                displayName: data.display_name,
                preferences: data.preferences || defaultPreferences,
                createdAt: data.created_at
            };
        } catch (err) {
            console.error('Profile fetch error:', err);
            return null;
        }
    }, []);

    // Initialize auth state
    useEffect(() => {
        if (!supabase) {
            setIsLoading(false);
            return;
        }

        let isSubscribed = true;
        let hadInitialSession = false;

        // Register the auth state listener FIRST (recommended by Supabase)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (!isSubscribed) return;

            console.log('Auth state changed:', event, session?.user?.email);

            // Track if we've had the initial session
            if (event === 'INITIAL_SESSION') {
                hadInitialSession = true;
            }

            // Skip SIGNED_IN if we haven't had INITIAL_SESSION yet (it fires too early after F5)
            if (event === 'SIGNED_IN' && !hadInitialSession) {
                console.log('Skipping early SIGNED_IN, waiting for INITIAL_SESSION');
                return;
            }

            // Always update session and user
            setSession(session);
            setUser(session?.user ?? null);

            if (session?.user) {
                // Only fetch profile if user ID changed or it's a relevant event
                const shouldFetchProfile = previousUserIdRef.current !== session.user.id;
                previousUserIdRef.current = session.user.id;

                if (shouldFetchProfile || event === 'INITIAL_SESSION' || (event === 'SIGNED_IN' && hadInitialSession)) {
                    console.log('Fetching profile for:', session.user.email);
                    const profile = await fetchProfile(session.user.id, session.user.email || '');
                    if (isSubscribed && profile) {
                        console.log('Profile loaded:', profile?.displayName);
                        setProfile(profile);
                    }
                }
            } else {
                previousUserIdRef.current = null;
                setProfile(null);
            }

            setIsLoading(false);
        });

        // THEN call getSession to trigger the INITIAL_SESSION event
        supabase.auth.getSession().then(({ error }) => {
            if (error) {
                console.error('Error getting session:', error);
                setIsLoading(false);
            }
            // The onAuthStateChange listener will handle the session
        });

        return () => {
            isSubscribed = false;
            subscription.unsubscribe();
        };
    }, [fetchProfile]);

    // Sign in with email/password
    const signInWithEmail = useCallback(async (email: string, password: string) => {
        if (!supabase) return { success: false, error: 'Supabase non configuré' };

        try {
            setError(null);
            const { error } = await supabase.auth.signInWithPassword({ email, password });

            if (error) {
                const translatedError = translateError(error.message);
                setError(translatedError);
                return { success: false, error: translatedError };
            }

            return { success: true };
        } catch (err) {
            const message = err instanceof Error ? translateError(err.message) : 'Erreur de connexion';
            setError(message);
            return { success: false, error: message };
        }
    }, []);

    // Sign up with email/password
    const signUpWithEmail = useCallback(async (email: string, password: string, displayName?: string) => {
        if (!supabase) return { success: false, error: 'Supabase non configuré' };

        try {
            setError(null);
            const { error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        display_name: displayName || email.split('@')[0]
                    }
                }
            });

            if (error) {
                setError(error.message);
                return { success: false, error: error.message };
            }

            return { success: true };
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Erreur d\'inscription';
            setError(message);
            return { success: false, error: message };
        }
    }, []);

    // Sign in with Magic Link
    const signInWithMagicLink = useCallback(async (email: string) => {
        if (!supabase) return { success: false, error: 'Supabase non configuré' };

        try {
            setError(null);
            const { error } = await supabase.auth.signInWithOtp({
                email,
                options: {
                    emailRedirectTo: `${window.location.origin}/`
                }
            });

            if (error) {
                setError(error.message);
                return { success: false, error: error.message };
            }

            return { success: true };
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Erreur d\'envoi du lien';
            setError(message);
            return { success: false, error: message };
        }
    }, []);

    // Sign in with OAuth provider (Google, Facebook)
    const signInWithProvider = useCallback(async (provider: AuthProvider) => {
        if (!supabase) return { success: false, error: 'Supabase non configuré' };

        try {
            setError(null);
            const { error } = await supabase.auth.signInWithOAuth({
                provider: provider as Provider,
                options: {
                    redirectTo: `${window.location.origin}/`
                }
            });

            if (error) {
                setError(error.message);
                return { success: false, error: error.message };
            }

            return { success: true };
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Erreur de connexion';
            setError(message);
            return { success: false, error: message };
        }
    }, []);

    // Sign out
    const signOut = useCallback(async () => {
        if (!supabase) return;

        try {
            // Create a timeout promise
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Sign out timeout')), 3000);
            });

            // Race between sign out and timeout
            await Promise.race([
                supabase.auth.signOut(),
                timeoutPromise
            ]);
        } catch (err) {
            console.warn('Sign out warning (continuing to clear local state):', err);
        } finally {
            // Always clear local state
            setUser(null);
            setSession(null);
            setProfile(null);
            setError(null);
            previousUserIdRef.current = null;

            // Clear any local storage auth items if needed
            // localStorage.removeItem('sb-...'); // Managed by Supabase client
        }
    }, []);

    // Reset password
    const resetPassword = useCallback(async (email: string) => {
        if (!supabase) return { success: false, error: 'Supabase non configuré' };

        try {
            setError(null);
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/settings`
            });

            if (error) {
                setError(error.message);
                return { success: false, error: error.message };
            }

            return { success: true };
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Erreur d\'envoi';
            setError(message);
            return { success: false, error: message };
        }
    }, []);

    // Update password
    const updatePassword = useCallback(async (newPassword: string) => {
        if (!supabase) return { success: false, error: 'Supabase non configuré' };

        try {
            setError(null);
            const { error } = await supabase.auth.updateUser({ password: newPassword });

            if (error) {
                setError(error.message);
                return { success: false, error: error.message };
            }

            return { success: true };
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Erreur de mise à jour';
            setError(message);
            return { success: false, error: message };
        }
    }, []);

    // Update profile
    const updateProfile = useCallback(async (data: Partial<UserProfile>) => {
        if (!supabase || !user) return { success: false, error: 'Non connecté' };

        try {
            const { error } = await supabase
                .from('profiles')
                .update({
                    display_name: data.displayName,
                    updated_at: new Date().toISOString()
                })
                .eq('id', user.id);

            if (error) {
                return { success: false, error: error.message };
            }

            setProfile(prev => prev ? { ...prev, ...data } : null);
            return { success: true };
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Erreur de mise à jour';
            return { success: false, error: message };
        }
    }, [user]);

    // Update preferences (synced to cloud)
    const updatePreferences = useCallback(async (prefs: Partial<UserPreferences>) => {
        if (!supabase || !user || !profile) return { success: false, error: 'Non connecté' };

        const newPrefs = { ...profile.preferences, ...prefs };

        try {
            const { error } = await supabase
                .from('profiles')
                .update({
                    preferences: newPrefs,
                    updated_at: new Date().toISOString()
                })
                .eq('id', user.id);

            if (error) {
                return { success: false, error: error.message };
            }

            setProfile(prev => prev ? { ...prev, preferences: newPrefs } : null);
            return { success: true };
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Erreur de mise à jour';
            return { success: false, error: message };
        }
    }, [user, profile]);

    // Sync local data to cloud when user logs in
    const syncLocalDataToCloud = useCallback(async () => {
        if (!supabase || !user) return;

        try {
            console.log('Starting full sync to cloud...');

            // 1. Get local data
            const localFavorites = JSON.parse(localStorage.getItem('jem-favorites') || '[]');
            const localHistory = JSON.parse(localStorage.getItem('jem-history') || '[]');

            // Get local preferences
            const localTheme = localStorage.getItem('jem-theme') || 'dark';
            const localTextSize = localStorage.getItem('jem-text-size') || 'normal';
            const localMidiVolume = parseInt(localStorage.getItem('midiVolume') || '100');
            const localNotifications = JSON.parse(localStorage.getItem('jem-notifications-settings') || '{}');

            // 2. Sync preferences
            await updatePreferences({
                theme: localTheme as UserPreferences['theme'],
                textSize: localTextSize as UserPreferences['textSize'],
                midiVolume: localMidiVolume,
                notificationsEnabled: localNotifications.enabled || false,
                dailyReminderTime: localNotifications.reminderTime || null,
                songOfTheDayEnabled: localNotifications.songOfTheDayEnabled || false
            });
            console.log('Preferences synced');

            // 3. Sync favorites to cloud
            if (localFavorites.length > 0) {
                const { data: existingFavs } = await supabase
                    .from('user_favorites')
                    .select('song_id')
                    .eq('user_id', user.id);

                const existingSongIds = new Set((existingFavs || []).map(f => f.song_id));

                const newFavorites = localFavorites
                    .filter((f: { songId: number }) => !existingSongIds.has(f.songId))
                    .map((f: { songId: number }) => ({
                        user_id: user.id,
                        song_id: f.songId
                    }));

                if (newFavorites.length > 0) {
                    await supabase.from('user_favorites').insert(newFavorites);
                    console.log('Favorites synced:', newFavorites.length);
                }
            }

            // 4. Sync history to cloud (upsert to avoid duplicates)
            if (localHistory.length > 0) {
                for (const entry of localHistory.slice(0, 50)) { // Limit to 50 most recent
                    const { error } = await supabase
                        .from('user_history')
                        .upsert({
                            user_id: user.id,
                            song_id: entry.songId,
                            duration: entry.duration || 0,
                            viewed_at: new Date(entry.timestamp || Date.now()).toISOString()
                        }, {
                            onConflict: 'user_id,song_id'
                        });

                    if (error) {
                        console.warn('History sync error for song', entry.songId, ':', error.message);
                    }
                }
                console.log('History synced');
            }

            // 5. Sync playlists from IndexedDB
            try {
                const { exportPlaylists } = await import('@/hooks/usePlaylists');
                const localPlaylists = await exportPlaylists();

                if (localPlaylists.length > 0) {
                    for (const playlist of localPlaylists) {
                        const { error } = await supabase
                            .from('user_playlists')
                            .upsert({
                                id: playlist.id,
                                user_id: user.id,
                                name: playlist.name,
                                songs: playlist.songNumbers,
                                created_at: new Date(playlist.createdAt).toISOString(),
                                updated_at: new Date(playlist.updatedAt).toISOString()
                            }, {
                                onConflict: 'id'
                            });

                        if (error) {
                            console.warn('Playlist sync error for', playlist.name, ':', error.message);
                        }
                    }
                    console.log('Playlists synced:', localPlaylists.length);
                }
            } catch (playlistError) {
                console.warn('Could not sync playlists:', playlistError);
            }

            console.log('Full sync completed!');
        } catch (err) {
            console.error('Error syncing local data:', err);
        }
    }, [user, updatePreferences]);

    // Delete user account and all data
    const deleteAccount = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
        if (!supabase || !user) {
            return { success: false, error: 'Non connecté' };
        }

        try {
            setError(null);

            // Delete all user data from tables
            const userId = user.id;

            // Delete user data from all tables
            await supabase.from('user_favorites').delete().eq('user_id', userId);
            await supabase.from('user_history').delete().eq('user_id', userId);
            await supabase.from('user_playlists').delete().eq('user_id', userId);
            await supabase.from('profiles').delete().eq('id', userId);

            // Call the Edge Function or RPC to delete auth user
            // Since we can't use admin.deleteUser from client, we use signOut
            // The actual user deletion should be handled by Supabase RPC or Edge Function
            const { error: rpcError } = await supabase.rpc('delete_user');

            if (rpcError) {
                console.warn('RPC delete_user not available, signing out:', rpcError);
            }

            // Sign out and clear local state
            await supabase.auth.signOut();

            // Clear local state
            setUser(null);
            setSession(null);
            setProfile(null);
            previousUserIdRef.current = null;

            // Clear local storage
            localStorage.removeItem('jem-favorites');
            localStorage.removeItem('jem-history');
            localStorage.removeItem('jem-settings');

            return { success: true };
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Erreur de suppression';
            console.error('Delete account error:', err);
            setError(message);
            return { success: false, error: message };
        }
    }, [user]);

    const value: AuthContextType = {
        user,
        profile,
        session,
        isLoading,
        isAuthenticated,
        isConfigured,
        error,
        signInWithEmail,
        signUpWithEmail,
        signInWithMagicLink,
        signInWithProvider,
        signOut,
        deleteAccount,
        resetPassword,
        updatePassword,
        updateProfile,
        updatePreferences,
        syncLocalDataToCloud
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
